"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.Carousel = void 0;
const jsx_runtime_1 = require("@emotion/react/jsx-runtime");
const app_1 = require("@obvio/app");
const utils_1 = require("@obvio/utils");
const react_1 = require("react");
const react_transition_group_1 = require("react-transition-group");
const CarouselComponents_1 = require("./CarouselComponents");
const CarouselLoader_1 = require("./CarouselLoader");
const Swipe_1 = require("../Swipe");
const StyledCarousel = app_1.styled.div `
  position: relative;
  width: 100%;
  overflow: hidden;
  display: flex;
  justify-content: space-between;

  .carousel-item-enter {
    transform: translateX(100%);
  }
  .carousel-item-enter-active {
    transform: translateX(0) !important;
    transition: transform ease;
  }
  .carousel-item-exit-active {
    transition: transform ease;
    transform: translateX(-100%);
  }
`;
const frameMarginStyle = (0, app_1.css) `
  margin: 1rem;
`;
const StyledArrow = app_1.styled.div `
  cursor: pointer;
  opacity: 0.7;
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
`;
const StyledLeftArrow = (0, app_1.styled)(StyledArrow) `
  left: 1.25rem;
  border-right: 1.25rem solid red;
  border-top: 1.25rem solid transparent;
  border-bottom: 1.25rem solid transparent;
`;
const StyledRightArrow = (0, app_1.styled)(StyledArrow) `
  right: 1.25rem;
  border-left: 1.25rem solid white;
  border-top: 1.25rem solid transparent;
  border-bottom: 1.25rem solid transparent;
`;
const StyledDots = app_1.styled.div `
  position: absolute;
  bottom: 1.25rem;
  left: 50%;
  transform: translateX(-50%);
  > button {
    all: unset;
    display: inline-block;
    cursor: pointer;
    margin: 0.625rem;
    width: 0.4375rem;
    height: 0.4375rem;
    border-radius: 50%;
    background: white;
    opacity: 0.6;
    box-shadow: 0.125rem 0.125rem 0.3125rem rgba(0, 0, 0, 0.3);
    &:hover {
      opacity: 0.9;
    }
    position: relative;
    &:before {
      content: '';
      position: absolute;
      width: 100%;
      height: 100%;
      transform: scale(3, 5);
    }
  }
`;
const StyledHiddenElements = app_1.styled.div `
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  opacity: 0;
  pointer-events: none;
  z-index: -1000;
  > div {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
  }
`;
const defaultHeightStyle = (0, app_1.css) `
  height: 50vh;
`;
const reverseCarouselCss = (0, app_1.css) `
  .carousel-item-enter {
    transform: translateX(-100%);
  }
  .carousel-item-exit-active {
    transform: translateX(100%);
  }
`;
const activeDotCss = (0, app_1.css) `
  opacity: 0.9 !important;
`;
const defaultOptions = {
    pauseOnHover: true,
    showArrows: true,
    showControls: true,
    autoHeight: false,
    preloadElements: false,
    withSwipe: true,
    noAutoSwitch: false,
};
function Carousel({ transitionDuration = 400, switchTimeout = 7000, loading, renderLoading: Loader = CarouselLoader_1.CarouselLoader, elements, render, options: { pauseOnHover, showArrows, showControls, autoHeight, preloadElements, withSwipe, noAutoSwitch, } = defaultOptions, swipeBoundary, renderArrows: Arrows, enhance, renderControls: Controls, className, frames = 1, }) {
    const [isMounted, setMounted] = (0, react_1.useState)(false);
    const [swiping, setSwiping] = (0, react_1.useState)(false);
    const container = (0, react_1.useRef)(null);
    const swipeLock = (0, react_1.useRef)(false);
    const [current, setCurrent] = (0, react_1.useState)(0);
    const [reverse, setReverse] = (0, react_1.useState)(false);
    const timeoutId = (0, react_1.useRef)();
    const [currentHeight, setCurrentHeight] = (0, react_1.useState)(0);
    const hiddenElements = (0, react_1.useRef)(null);
    const handleSwitch = (0, react_1.useCallback)((callback, shouldReverse) => {
        setReverse(!!shouldReverse);
        setCurrent((currentIndex) => (callback === undefined ? currentIndex + 1 : callback(currentIndex)) %
            elements.length);
    }, [elements.length]);
    const stop = (0, react_1.useCallback)(() => {
        if (timeoutId.current !== undefined) {
            clearTimeout(timeoutId.current);
        }
    }, []);
    const roll = (0, react_1.useCallback)(() => {
        stop();
        if (!noAutoSwitch) {
            timeoutId.current = setTimeout(() => handleSwitch(), switchTimeout);
        }
    }, [handleSwitch, switchTimeout, stop, noAutoSwitch]);
    (0, react_1.useEffect)(() => {
        if (swiping) {
            stop();
        }
        else {
            clearSwipeStyles(container.current);
            roll();
            return stop;
        }
    }, [current, roll, stop, swiping]);
    (0, react_1.useEffect)(() => {
        if (!swiping) {
            swipeLock.current = false;
        }
    }, [swiping]);
    const { width = 0 } = (0, app_1.useWindowSize)(autoHeight ? { width: true } : {});
    (0, react_1.useEffect)(() => {
        var _a, _b, _c;
        if (!hiddenElements.current || !autoHeight) {
            return;
        }
        let maxHeight = 0;
        const { children } = hiddenElements.current;
        for (let i = 0; i < children.length; i += 1) {
            const element = (_b = (_a = children.item(i)) === null || _a === void 0 ? void 0 : _a.children) === null || _b === void 0 ? void 0 : _b.item(0);
            const h = (_c = element === null || element === void 0 ? void 0 : element.clientHeight) !== null && _c !== void 0 ? _c : 0;
            if (h > maxHeight) {
                maxHeight = h;
            }
        }
        setCurrentHeight(maxHeight);
        setMounted(true);
    }, [width, autoHeight]);
    const previous = () => {
        stop();
        handleSwitch((currentIndex) => currentIndex + elements.length - 1, true);
    };
    const next = () => {
        stop();
        handleSwitch();
    };
    const handleClick = (index) => {
        stop();
        if (index !== current) {
            handleSwitch(() => index, index < current);
        }
    };
    const handleSwipeStart = () => {
        if (withSwipe &&
            !swiping &&
            !swipeLock.current &&
            elements.length > 1 &&
            container.current &&
            !isSwitching(container.current)) {
            stop();
            setSwiping(true);
        }
    };
    const handleSwipeMove = ({ offset }) => {
        if (!swipeLock.current && swiping) {
            forSwipeChildren(container.current, (element) => {
                const el = element;
                el.style.transform = `translateX(${offset}px)`;
            });
        }
    };
    const handleSwipeEnd = ({ offset, execute }) => {
        if (container.current && !swipeLock.current && swiping) {
            const containerWidth = container.current.clientWidth;
            let time = Math.max(100, (Math.round((Math.abs(offset) * 100) / containerWidth) / 100) *
                transitionDuration);
            if (execute) {
                time = transitionDuration - time;
            }
            swipeLock.current = true;
            forSwipeChildren(container.current, (element) => {
                const el = element;
                el.style.transition = `transform ${time}ms ease`;
                el.style.transform = `translateX(${execute ? `${Math.sign(offset) < 0 ? '-' : ''}100%` : '0'})`;
            });
            setTimeout(() => {
                if (execute) {
                    if (offset > 0) {
                        previous();
                    }
                    else {
                        next();
                    }
                }
                setSwiping(false);
            }, time);
        }
    };
    const carousel = ((0, jsx_runtime_1.jsx)(Swipe_1.Swipe, Object.assign({ onSwipeStart: handleSwipeStart, onSwipeMove: handleSwipeMove, onSwipeEnd: handleSwipeEnd, swipeBoundary: swipeBoundary }, { children: (0, jsx_runtime_1.jsx)(StyledCarousel, Object.assign({ ref: container, css: (0, app_1.css) `
          .carousel-item-enter-active,
          .carousel-item-exit-active {
            transition-duration: ${transitionDuration}ms;
          }
          ${reverse && reverseCarouselCss}
          ${!autoHeight && defaultHeightStyle}
        `, className: className }, (pauseOnHover ? { onMouseEnter: stop, onMouseLeave: roll } : {}), { style: autoHeight ? { minHeight: currentHeight } : undefined }, { children: loading ? ((0, jsx_runtime_1.jsx)(Loader, { frames: frames })) : ((0, jsx_runtime_1.jsxs)(jsx_runtime_1.Fragment, { children: [Array.from({ length: frames }).map((_v, index) => {
                        const mainIndex = (index + current) % elements.length;
                        const prevIndex = (current + index + elements.length - 1) % elements.length;
                        const nextIndex = (current + index + 1) % elements.length;
                        if (elements.length === 0) {
                            return null;
                        }
                        return ((0, jsx_runtime_1.jsxs)(CarouselComponents_1.Frame, Object.assign({ css: (0, app_1.css) `
                    ${frames !== 1 && frameMarginStyle}
                  `, "data-type": "frame" }, { children: [swiping && ((0, jsx_runtime_1.jsx)(CarouselComponents_1.CarouselItem, Object.assign({ "data-swipe": "", css: (0, app_1.css) `
                        right: 100%;
                      ` }, { children: render(elements[prevIndex]) }))), (0, jsx_runtime_1.jsx)(react_transition_group_1.TransitionGroup, Object.assign({ component: null }, { children: (0, jsx_runtime_1.jsx)(react_transition_group_1.CSSTransition, Object.assign({ timeout: transitionDuration + 100, classNames: swiping ? 'swipe-item' : 'carousel-item' }, { children: (0, jsx_runtime_1.jsx)(CarouselComponents_1.CarouselItem, Object.assign({}, (swiping ? { 'data-swipe': '' } : {}), { style: autoHeight && !isMounted
                                                ? { position: 'static' }
                                                : undefined, css: (0, app_1.css) `
                          right: 0%;
                        ` }, { children: render(elements[mainIndex]) })) }), elements[mainIndex].id) })), swiping && ((0, jsx_runtime_1.jsx)(CarouselComponents_1.CarouselItem, Object.assign({ "data-swipe": "", css: (0, app_1.css) `
                        left: 100%;
                      ` }, { children: render(elements[nextIndex]) })))] }), index));
                    }), (autoHeight || preloadElements) && ((0, jsx_runtime_1.jsx)(StyledHiddenElements, Object.assign({ ref: hiddenElements }, { children: elements.map((element) => ((0, jsx_runtime_1.jsx)("div", { children: render(element) }, element.id))) }))), elements.length > 1 && ((0, jsx_runtime_1.jsxs)(jsx_runtime_1.Fragment, { children: [showArrows &&
                                (Arrows ? ((0, jsx_runtime_1.jsx)(Arrows, { previous: switchHandler(previous, swipeLock, swiping), next: switchHandler(next, swipeLock, swiping) })) : ((0, jsx_runtime_1.jsxs)(jsx_runtime_1.Fragment, { children: [(0, jsx_runtime_1.jsx)(StyledLeftArrow, { onClick: switchHandler(previous, swipeLock, swiping) }), (0, jsx_runtime_1.jsx)(StyledRightArrow, { onClick: switchHandler(next, swipeLock, swiping) })] }))), showControls &&
                                (Controls ? ((0, jsx_runtime_1.jsx)(Controls, { length: elements.length, activeIndex: current, switchTo: switchHandler(handleClick, swipeLock, swiping) })) : ((0, jsx_runtime_1.jsx)(StyledDots, { children: elements.map((el, i) => ((0, jsx_runtime_1.jsx)("button", { onClick: () => switchHandler(handleClick, swipeLock, swiping)(i), css: (0, app_1.css) `
                            ${current === i && activeDotCss}
                          `, type: "button", "aria-label": `select slide number ${i}` }, el.id))) })))] }))] })) })) })));
    return enhance
        ? enhance({
            carousel,
            previous,
            next,
            length: elements.length,
            activeIndex: current,
            switchTo: switchHandler(handleClick, swipeLock, swiping),
        })
        : carousel;
}
exports.Carousel = Carousel;
function forSwipeChildren(parent, callback) {
    if (parent) {
        const children = [...parent.children];
        children
            .reduce((acc, el) => [...acc, ...el.children], [])
            .forEach((child) => {
            if (child instanceof HTMLElement &&
                (0, utils_1.has)(child.dataset, 'swipe') &&
                !child.classList.contains('swipe-item-exit-active')) {
                callback(child);
            }
        });
    }
}
function isSwitching(parent) {
    return ([...parent.children]
        .reduce((acc, el) => [...acc, ...el.children], [])
        .findIndex((child) => child.classList.contains('carousel-item-enter-active') ||
        child.classList.contains('carousel-item-exit')) > -1);
}
function switchHandler(callback, swipeLock, swiping) {
    return swiping
        ? (() => null)
        : ((...args) => {
            if (!swipeLock.current) {
                callback(...args);
            }
        });
}
function clearSwipeStyles(container) {
    if (container) {
        const children = [...container.children];
        children
            .reduce((acc, el) => [...acc, ...el.children], [])
            .forEach((child) => {
            if (child instanceof HTMLElement &&
                !child.classList.contains('swipe-item-exit-active')) {
                child.style.removeProperty('transition');
                child.style.removeProperty('transform');
            }
        });
    }
}
